import { useEffect } from 'react';
import { usePlayer as useReactPlayer } from '@landr/react-player';
import { StreamEntityType } from 'types/enums';
import { selectSample, demoPlaybackStarted } from 'helpers/dawPlugin';
import useStream from './hooks/useStream';
import usePlayerActions from './hooks/usePlayerActions';
import useError from './hooks/useError';
import useExpiration from './hooks/useExpiration';
import { PlayerContextType } from './types';

// Stream url expired after 5 minutes - we want to refetch a new stream url before that
const STREAM_URL_EXPIRATION = 4 * 60 * 1000;

export const usePlayerProvider = ({
    streamEntityType,
    id,
    customStreamUrl,
    notifyIsPlaying,
}: {
    streamEntityType: StreamEntityType;
    id?: string;
    customStreamUrl?: string;
    notifyIsPlaying?: (isPlaying: boolean) => void;
}): PlayerContextType => {
    const {
        currentTime,
        error: reactPlayerError,
        loading: reactPlayerLoading,
        playing,
        totalTime,
        seek,
        setLoading,
        setPlaying,
        setSources,
    } = useReactPlayer(id || null);

    useEffect(() => {
        notifyIsPlaying?.(playing);
    }, [playing, notifyIsPlaying]);

    const { onError } = useError({
        setSources,
        setLoading,
        setPlaying,
    });

    useEffect(() => {
        if (reactPlayerError) {
            onError({
                error: reactPlayerError,
                errorMessage: 'React Player internal error',
            });
        }
    }, [reactPlayerError, onError]);

    const { getIsExpired, updateExpirationDate } = useExpiration({
        expiration: STREAM_URL_EXPIRATION,
    });

    const { playStream } = useStream({
        seek,
        setPlaying,
        id,
        streamEntityType,
        setSources,
        onError,
        getIsExpired,
        updateExpirationDate,
        setLoading,
        customStreamUrl,
    });

    const { playFromBeginning, togglePlaying } = usePlayerActions({
        id,
        playing,
        streamEntityType,
        totalTime,
        demoPlaybackStarted,
        selectSample,
        setPlaying,
        playStream,
    });

    const isLoading = reactPlayerLoading;

    return {
        currentTime,
        loading: isLoading,
        playing,
        totalTime,
        togglePlaying,
        playFromBeginning,
        seek,
    };
};
